import { memo, useMemo, useState } from 'react';
import { ChartWrapper } from '../shared/ChartWrapper';
import { useOverviewData } from './useOverviewData';
import { useApp } from '../../../context/AppContext';
import { TRAFFIC_HEX } from '../shared/constants';

const IA_COLORS = {
  poverty: '#007DBC',
  nutrition: '#F37021',
  biodiversity: '#14B8A6',
  gender: '#8B5CF6',
  climate: '#006837',
};

const IA_LABELS = {
  poverty: 'Poverty Reduction, Livelihoods & Jobs',
  nutrition: 'Nutrition, Health & Food Security',
  biodiversity: 'Environmental Health & Biodiversity',
  gender: 'Gender Equality, Youth & Social Inclusion',
  climate: 'Climate Adaptation & Mitigation',
};

const IA_SHORT = {
  poverty: 'Poverty',
  nutrition: 'Nutrition',
  biodiversity: 'Biodiversity',
  gender: 'Gender',
  climate: 'Climate',
};

const IA_NAME_TO_KEY = {
  'poverty reduction, livelihoods and jobs': 'poverty',
  'nutrition, health and food security': 'nutrition',
  'environmental health and biodiversity': 'biodiversity',
  'gender equality, youth and social inclusion': 'gender',
  'climate adaptation and mitigation': 'climate',
};

function matchIaKey(iaName) {
  return IA_NAME_TO_KEY[(iaName || '').toLowerCase().trim()] || null;
}

function truncate(text, maxLen) {
  if (!text || text.length <= maxLen) return text || '';
  return text.slice(0, maxLen - 1) + '\u2026';
}

function extractOcLabel(title) {
  const m = title.match(/^(2030[\s-]*OC[\s-]*\d+)/i);
  return m ? m[1].replace(/\s+/g, ' ') : null;
}

function extractAowLabel(name) {
  const m = (name || '').match(/^(AOW[\s-]*\d+)/i);
  return m ? m[1].replace(/[\s-]+/g, '') : null;
}

export const HighLevelTocDiagram = memo(function HighLevelTocDiagram() {
  const { areasOfWork, byImpactArea } = useOverviewData();
  const { tocData, filters } = useApp();
  const [tip, setTip] = useState('');
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  const programCode = filters?.program !== 'all' ? filters?.program : null;
  const programToc = programCode ? tocData?.[programCode] : null;

  const outcomes2030 = useMemo(() => programToc?.outcomes2030 || [], [programToc]);
  const tocImpactAreas = useMemo(() => programToc?.impactAreas || [], [programToc]);

  // Map AoW name → { color, label } using traffic light rating
  const aowColorMap = useMemo(() => {
    const map = {};
    areasOfWork.forEach((aow, i) => {
      map[aow.name] = {
        color: TRAFFIC_HEX[aow.ratingKey] || '#9ca3af',
        label: extractAowLabel(aow.name) || `AOW${String(i + 1).padStart(2, '0')}`,
      };
    });
    return map;
  }, [areasOfWork]);

  // Sort outcomes by their first linked AoW (same order as AoW column), then by title
  const sortedOutcomes = useMemo(() => {
    const items = [...outcomes2030];
    const aowIndex = (name) => {
      const idx = areasOfWork.findIndex(aow => aow.name === name);
      return idx === -1 ? 999 : idx;
    };
    items.sort((a, b) => {
      const aMin = Math.min(...(a.aows || []).map(aowIndex), 999);
      const bMin = Math.min(...(b.aows || []).map(aowIndex), 999);
      if (aMin !== bMin) return aMin - bMin;
      return (a.title || '').localeCompare(b.title || '', undefined, { numeric: true });
    });
    return items;
  }, [outcomes2030, areasOfWork]);

  const iaData = useMemo(() => {
    const standard = ['climate', 'nutrition', 'biodiversity', 'gender', 'poverty'];
    return standard.map(key => {
      const counts = byImpactArea?.[key] || { 0: 0, 1: 0, 2: 0 };
      const tocIA = tocImpactAreas.find(ia => matchIaKey(ia.name) === key);
      return {
        key,
        label: IA_LABELS[key],
        short: IA_SHORT[key],
        color: IA_COLORS[key],
        principal: counts[2] || 0,
        significant: counts[1] || 0,
        globalTarget: tocIA?.globalTarget || '',
        description: tocIA?.description || '',
      };
    });
  }, [byImpactArea, tocImpactAreas]);

  if (!areasOfWork.length) {
    return (
      <ChartWrapper title="High-Level Theory of Change" subtitle="Select a specific program to view the ToC overview">
        <div className="flex items-center justify-center h-48 text-[var(--text-tertiary)] text-sm">
          Select a specific program to display the high-level Theory of Change diagram.
        </div>
      </ChartWrapper>
    );
  }

  const colW = 'calc(33.333% - 2rem)';

  return (
    <ChartWrapper title="High-Level Theory of Change" subtitle="Areas of Work → 2030 Outcomes → Impact Areas">
      <div className="relative flex gap-4 items-start min-h-[320px]">
        {/* Column 1: Sphere of Control — AoWs */}
        <div className="flex-1 min-w-0">
          <div className="text-center mb-3">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
              Sphere of Control
            </span>
            <div className="text-[11px] text-[var(--text-tertiary)] mt-1 font-medium">Areas of Work</div>
          </div>
          <div className="space-y-2 pr-1">
            {areasOfWork.map(aow => {
              const bgColor = TRAFFIC_HEX[aow.ratingKey] || '#9ca3af';
              const pct = aow.achievementPct != null ? Math.round(aow.achievementPct) : null;
              return (
                <div
                  key={aow.id}
                  className="rounded-lg border border-[var(--border-primary)] overflow-hidden bg-[var(--surface-primary)] shadow-sm"
                  onMouseEnter={e => { setTip(aow.name); setTipPos({ x: e.clientX, y: e.clientY }); }}
                  onMouseMove={e => setTipPos({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTip('')}
                >
                  <div className="px-3 py-1.5 text-white text-xs font-semibold" style={{ backgroundColor: bgColor }}>
                    {truncate(aow.name, 50)}
                  </div>
                  <div className="px-3 py-2 flex items-center justify-between gap-2">
                    <span className="text-xs text-[var(--text-secondary)]">
                      <span className="font-semibold text-[var(--text-primary)]">{aow.resultCount ?? 0}</span> results
                    </span>
                    {pct != null && (
                      <span className="text-xs font-medium" style={{ color: bgColor }}>
                        {pct}% achieved
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Arrow column 1→2 */}
        <div className="flex flex-col items-center justify-center w-8 flex-shrink-0">
          <div className="flex-1 w-px bg-gradient-to-b from-transparent via-[var(--border-primary)] to-transparent" />
          <svg className="w-6 h-6 text-[var(--text-tertiary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
          <div className="flex-1 w-px bg-gradient-to-b from-transparent via-[var(--border-primary)] to-transparent" />
        </div>

        {/* Column 2: Sphere of Influence — 2030 Outcomes */}
        <div className="flex-[1.4] min-w-0">
          <div className="text-center mb-3">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
              Sphere of Influence
            </span>
            <div className="text-[11px] text-[var(--text-tertiary)] mt-1 font-medium">2030 Outcomes</div>
          </div>
          <div className="space-y-2 pr-1">
            {sortedOutcomes.length > 0 ? sortedOutcomes.map((oc, i) => {
              const label = extractOcLabel(oc.title);
              const statementClean = oc.statement
                ? oc.statement.replace(/^2030[\s-]*OC[\s-]*\d+\.?\s*/i, '').trim()
                : '';
              const aowInfos = (oc.aows || []).map(name => aowColorMap[name]).filter(Boolean);
              return (
                <div
                  key={i}
                  className="rounded-lg border border-blue-200 bg-blue-50/50 p-3 shadow-sm"
                  onMouseEnter={e => { setTip(oc.statement || oc.title); setTipPos({ x: e.clientX, y: e.clientY }); }}
                  onMouseMove={e => setTipPos({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTip('')}
                >
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    {label && (
                      <span className="text-[10px] font-bold text-blue-700">{label}</span>
                    )}
                    {aowInfos.map(info => (
                      <span
                        key={info.label}
                        className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold text-white leading-none"
                        style={{ backgroundColor: info.color }}
                      >
                        {info.label}
                      </span>
                    ))}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] leading-relaxed">
                    {statementClean || oc.title}
                  </div>
                </div>
              );
            }) : (
              <div className="text-xs text-[var(--text-tertiary)] text-center py-6 italic">
                No 2030 Outcome data available for this program.
              </div>
            )}
          </div>
        </div>

        {/* Arrow column 2→3 */}
        <div className="flex flex-col items-center justify-center w-8 flex-shrink-0">
          <div className="flex-1 w-px bg-gradient-to-b from-transparent via-[var(--border-primary)] to-transparent" />
          <svg className="w-6 h-6 text-[var(--text-tertiary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
          <div className="flex-1 w-px bg-gradient-to-b from-transparent via-[var(--border-primary)] to-transparent" />
        </div>

        {/* Column 3: Sphere of Interest — Impact Areas */}
        <div className="flex-1 min-w-0">
          <div className="text-center mb-3">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
              Sphere of Interest
            </span>
            <div className="text-[11px] text-[var(--text-tertiary)] mt-1 font-medium">Impact Areas</div>
          </div>
          <div className="space-y-2 pr-1">
            {iaData.map(ia => (
              <div
                key={ia.key}
                className="rounded-lg border overflow-hidden bg-[var(--surface-primary)] shadow-sm"
                style={{ borderColor: ia.color + '40' }}
                onMouseEnter={e => {
                  const tipText = ia.globalTarget
                    ? `${ia.label}\n\nGlobal Target: ${ia.globalTarget}`
                    : ia.label;
                  setTip(tipText);
                  setTipPos({ x: e.clientX, y: e.clientY });
                }}
                onMouseMove={e => setTipPos({ x: e.clientX, y: e.clientY })}
                onMouseLeave={() => setTip('')}
              >
                <div className="px-3 py-1.5 text-white text-xs font-semibold" style={{ backgroundColor: ia.color }}>
                  {ia.short}
                </div>
                <div className="px-3 py-2">
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-[var(--text-secondary)]">
                      P: <span className="font-semibold text-[var(--text-primary)]">{ia.principal}</span>
                    </span>
                    <span className="text-[var(--text-secondary)]">
                      S: <span className="font-semibold text-[var(--text-primary)]">{ia.significant}</span>
                    </span>
                  </div>
                  <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
                    P = Principal (2), S = Significant (1)
                  </div>
                  {ia.globalTarget && (
                    <div className="text-[10px] text-[var(--text-secondary)] mt-1 leading-snug">
                      {truncate(ia.globalTarget, 150)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tooltip */}
      {tip && (
        <div
          className="fixed z-50 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none max-w-xs whitespace-pre-line"
          style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}
        >
          {tip}
        </div>
      )}
    </ChartWrapper>
  );
});
